export const DEFAULT_MAX_LENGTH = 5000;
const SHOW_COUNTER_THRESHOLD = 0.98;
const WARNING_CLASS = 'sd-character-counter--warning';

/**
 * Renders a character counter for the "other" text field in radiogroup, checkbox, and dropdown questions.
 * Counter is shown only when input approaches the limit (SHOW_COUNTER_THRESHOLD).
 * SurveyJS enforces the hard limit via model.maxOthersLength; this only adds the visual counter.
 * @param {HTMLElement} container - The question's root DOM element.
 * @param {object} question - The SurveyJS question model.
 * @returns {boolean} - Returns true if the counter was attached.
 */
export function renderOtherFieldCounter(container, question) {
  const otherText = container.querySelector('.sd-selectbase__other');
  if (!otherText || otherText.dataset.counterInit) return false;
  otherText.dataset.counterInit = 'true';

  const maxLength = question.jsonObj?.maxLength || DEFAULT_MAX_LENGTH;
  const showAt = Math.floor(maxLength * SHOW_COUNTER_THRESHOLD);

  const counter = document.createElement('div');
  counter.className = 'sd-character-counter';
  otherText.parentNode.appendChild(counter);

  const update = () => {
    const length = otherText.value.length;
    const show = length >= showAt;
    counter.textContent = `${length}/${maxLength}`;
    counter.style.display = show ? '' : 'none';
    counter.classList.toggle(WARNING_CLASS, show);
  };

  update();
  otherText.addEventListener('input', update);
  return true;
}

/**
 * Shows the built-in SurveyJS character counter.
 * - When question has explicit "Maximum character limit": always show.
 * - When using global maxTextLength: show only when input exceeds the threshold (SHOW_COUNTER_THRESHOLD).
 * @param {object} options - The options object provided by SurveyJS onAfterRenderQuestion.
 */
export function initBuiltInCounterWarning(options) {
  const type = options.question.getType();
  if (type !== 'text' && type !== 'comment') return;

  const input = options.htmlElement.querySelector('.sd-input');
  if (!input || input.dataset.warningInit) return;
  input.dataset.warningInit = 'true';

  const question = options.question;
  const hasExplicitMaxLength = question.maxLength > 0;
  const maxLength = hasExplicitMaxLength ? question.maxLength : (question.survey?.maxTextLength || DEFAULT_MAX_LENGTH);
  const showAt = Math.floor(maxLength * SHOW_COUNTER_THRESHOLD);

  const update = () => {
    const counter = options.htmlElement.querySelector('.sd-remaining-character-counter');
    if (counter) {
      const length = input.value.length;
      const nearLimit = length >= showAt;
      if (hasExplicitMaxLength) {
        counter.classList.toggle(WARNING_CLASS, nearLimit);
      } else {
        counter.style.display = nearLimit ? '' : 'none';
        counter.classList.toggle(WARNING_CLASS, nearLimit);
      }
    }
  };

  input.addEventListener('input', update);
  update();
}
